/**
 * Locale-aware SEO helpers.
 *
 * Toolverse serves 10 locales via URL prefixes (en is unprefixed; es → /es/…,
 * de → /de/…, etc.). For correct international SEO every page must emit:
 *   - a SELF-referencing canonical for the CURRENT locale, and
 *   - a reciprocal hreflang cluster covering ALL locales + x-default.
 *
 * Routes pass the de-localized ("base", English) path — always starting with
 * "/" — and these helpers produce the right absolute URLs per locale using
 * Paraglide's `localizeHref`. The hreflang set is identical across every locale
 * version of a page (a hard requirement for reciprocity).
 */
import { getLocale, locales, localizeHref } from "@/paraglide/runtime";

export const SITE_ORIGIN = "https://toolverse.me";

/** Paraglide locale → Open Graph `og:locale` code (language_TERRITORY). */
const OG_LOCALE: Record<string, string> = {
  en: "en_US",
  es: "es_ES",
  de: "de_DE",
  nl: "nl_NL",
  fr: "fr_FR",
  it: "it_IT",
  "pt-BR": "pt_BR",
  pt: "pt_PT",
  ja: "ja_JP",
  zh: "zh_CN",
};

/**
 * Absolute URL for a base path in a given locale (defaults to the CURRENT
 * locale). Use this for every URL inside JSON-LD (BreadcrumbList `item`,
 * `url`, `@id`): a localized page whose structured data points at the English
 * URL contradicts its own canonical, which reads as a duplicate to Google.
 */
export function localeUrl(
  basePath: string,
  locale: string = getLocale(),
): string {
  const path = basePath.startsWith("/") ? basePath : `/${basePath}`;
  const localized = localizeHref(path, { locale });
  return `${SITE_ORIGIN}${localized}`;
}

type LinkTag = { rel: string; hrefLang?: string; href: string };

/**
 * Canonical (self, current locale) + full reciprocal hreflang cluster.
 * Spread the result into a route's `head().links`.
 */
export function i18nLinks(basePath: string): LinkTag[] {
  const current = getLocale();
  const links: LinkTag[] = [
    { rel: "canonical", href: localeUrl(basePath, current) },
  ];
  for (const locale of locales) {
    links.push({
      rel: "alternate",
      hrefLang: locale,
      href: localeUrl(basePath, locale),
    });
  }
  // x-default points at the base-locale (English) URL.
  links.push({
    rel: "alternate",
    hrefLang: "x-default",
    href: localeUrl(basePath, "en"),
  });
  return links;
}

type MetaTag = { property?: string; name?: string; content: string };

/**
 * og:url (current locale) + og:locale. Spread into a route's `head().meta`.
 *
 * `og:locale:alternate` is deliberately NOT emitted here: TanStack Router
 * dedupes head meta by `property`, so nine alternates collapsed into one
 * (always zh_CN). They are rendered as plain JSX <meta> in the root shell
 * instead — see `ogLocaleAlternates()` + RootDocument in __root.tsx.
 */
export function i18nOgMeta(basePath: string): MetaTag[] {
  const current = getLocale();
  return [
    { property: "og:url", content: localeUrl(basePath, current) },
    { property: "og:locale", content: OG_LOCALE[current] ?? "en_US" },
  ];
}

/** og:locale codes for every locale EXCEPT the current one. */
export function ogLocaleAlternates(): string[] {
  const current = getLocale();
  return locales
    .filter((l) => l !== current)
    .map((l) => OG_LOCALE[l] ?? "en_US");
}

/**
 * FAQPage JSON-LD `head().scripts` entry for a page's visible Q&A block.
 * Google no longer shows FAQ rich results for general sites, but the markup
 * still gives AI search engines / Overviews clean question→answer pairs to
 * cite. Pass the SAME strings that are rendered on the page.
 */
export function faqPageScript(faqs: { q: string; a: string }[]): {
  type: string;
  children: string;
} {
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      inLanguage: getLocale(),
      mainEntity: faqs.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    }),
  };
}

/**
 * Convenience: canonical + hreflang links AND og url/locale meta for a path.
 * Returns `{ meta, links }` ready to merge into a route head.
 */
export function i18nSeo(basePath: string): {
  meta: MetaTag[];
  links: LinkTag[];
} {
  return { meta: i18nOgMeta(basePath), links: i18nLinks(basePath) };
}
